import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import axios from "../../utility/customAxios";
import {
    AlertTriangle,
    Archive,
    Bot,
    Check,
    CheckCircle,
    Circle,
    Clock,
    Edit2,
    FileText,
    GitMerge,
    GitPullRequest,
    MessageSquare,
    MoreHorizontal,
    Plus,
    Trash2,
    User,
    X
} from "lucide-react";
import { MarkdownRenderer } from "../Guardian/MarkdownRenderer";

import {
    deleteInvestigation,
    getInvestigation,
    listInvestigationComments,
    resolveInvestigation,
    unresolveInvestigation,
    updateInvestigation,
} from "../../clients/metoro/investigations";
import {Investigation} from "../../types/investigation";
import {withErrorHandling} from "../../utility/apiUtils";
import {LoadingSpinner} from "../LoadingSpinner";
import {Button} from "../ui/button";
import {Badge} from "../ui/badge";
import {Input} from "../ui/input";
import {Textarea} from "../ui/textarea";
import {useToast} from "../ui/use-toast";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../ui/alert-dialog";
import {DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,} from "../ui/dropdown-menu";
import InvestigationComments from "./InvestigationComments";
import {cn} from "../ui/lib/utils";
import {Tabs, TabsContent, TabsList, TabsTrigger} from "../ui/tabs";
import {Tag as TagComponent} from "../SidePanel/Tags/TagContainer";
import {Message} from "../Guardian/GuardianChat";
import CustomAxios from "../../utility/customAxios";
import { AiInvestigation } from "./AiInvestigation";
import { SingleSelectorSearchableDropdown } from "../Input/SingleSelectorSearchableDropdown";
import { DropDownItem } from "../Input/MultiSelectorDropdown/MultiSelectorDropDown";

// Priority configuration (matching the list view)
const PRIORITY_CONFIG = {
    P1: {label: "Critical", color: "bg-red-500", textColor: "text-red-500", icon: AlertTriangle},
    P2: {label: "High", color: "bg-orange-500", textColor: "text-orange-500", icon: AlertTriangle},
    P3: {label: "Medium", color: "bg-yellow-500", textColor: "text-yellow-500", icon: Circle},
    P4: {label: "Low", color: "bg-blue-500", textColor: "text-blue-500", icon: Circle},
    P5: {label: "Info", color: "bg-gray-500", textColor: "text-gray-500", icon: Circle}
};

// Status configuration
const STATUS_CONFIG = {
    active: {label: "Active", color: "bg-blue-500", icon: Circle},
    in_progress: {label: "In Progress", color: "bg-yellow-500", icon: Clock},
    resolved: {label: "Resolved", color: "bg-green-500", icon: CheckCircle},
    archived: {label: "Archived", color: "bg-gray-500", icon: Archive}
};


const InvestigationDetails: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const {toast} = useToast();
    const queryClient = useQueryClient();
    const investigationUuid = searchParams.get("uuid");

    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [editedTitle, setEditedTitle] = useState("");
    const [isEditingDetails, setIsEditingDetails] = useState(false);
    const [editedDetails, setEditedDetails] = useState("");
    const [detailsEditMode, setDetailsEditMode] = useState<"write" | "preview">("write");
    const [isAddingTag, setIsAddingTag] = useState(false);
    const [newTagKey, setNewTagKey] = useState("");
    const [newTagValue, setNewTagValue] = useState("");
    const [comments, setComments] = useState<string[]>([]);
    const [aiInvestigationMessages, setAiInvestigationMessages] = useState<Message[]>([]);
    const [organizationMembers, setOrganizationMembers] = useState<DropDownItem[]>([]);
    const [loadingMembers, setLoadingMembers] = useState(true);

    const {data: investigation, isLoading, error} = useQuery({
        queryKey: ["investigation", investigationUuid],
        queryFn: () =>
            investigationUuid
                ? withErrorHandling(() => getInvestigation(investigationUuid))
                : Promise.reject(new Error("No investigation UUID provided")),
        enabled: !!investigationUuid,
    });

    useEffect(() => {
        if (!investigationUuid) return;
        getInvestigation(investigationUuid)
            .then(investigation => {
                if (!investigation.chatHistoryUuid) {
                    return
                }
                CustomAxios.get(`/api/v1/guardian/chat?id=${investigation.chatHistoryUuid}`).then((response) => {
                    const data = response.data;
                    // Make sure we process the screenshots properly when loading chat history
                    const messagesWithFormattedScreenshots = data.messages.map((msg: any) => ({
                        ...msg
                        // Screenshot already comes as a base64 string, no need to transform
                    }));
                    setAiInvestigationMessages(messagesWithFormattedScreenshots);
                });
            }).catch(err => {
            console.error("Failed to fetch AI investigation messages:", err);
            toast({
                title: "Error",
                description: "Failed to load AI investigation messages.",
                variant: "destructive",
            });
        })
    }, [investigationUuid]);

    useEffect(() => {
        if (!investigationUuid) return;
        let newComments = [];
        listInvestigationComments(investigationUuid!).then((response) => {
                newComments = response.comments.map(comment => comment.content);
                setComments(newComments);
            }
        ).catch((err) => {
            console.error("Failed to fetch comments:", err);
            toast({
                title: "Error",
                description: "Failed to load comments for this investigation.",
                variant: "destructive",
            });
        });
    }, [])

    // Fetch organization members for assignee dropdown
    useEffect(() => {
        const fetchMembers = async () => {
            try {
                const response = await axios.get("/api/v1/account/organization");
                const members = response.data.organizationMembers.map((member: any) => ({
                    displayName: member.name || member.email,
                    value: member.email,
                }));
                // Add "Unassigned" option at the beginning
                const membersWithUnassigned = [
                    { displayName: "Unassigned", value: "" },
                    ...members
                ];
                setOrganizationMembers(membersWithUnassigned);
                setLoadingMembers(false);
            } catch (error) {
                console.error("Failed to fetch organization members:", error);
                setLoadingMembers(false);
            }
        };

        fetchMembers();
    }, []);

    const updateMutation = useMutation({
        mutationFn: (updates: Partial<Investigation>) =>
            withErrorHandling(() => updateInvestigation(investigationUuid!, updates)),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["investigation", investigationUuid]});
            toast({
                title: "Investigation updated",
                description: "Your changes have been saved.",
            });
        },
    });

    const resolveMutation = useMutation({
        mutationFn: (resolved: boolean) =>
            withErrorHandling(() =>
                resolved
                    ? resolveInvestigation(investigationUuid!)
                    : unresolveInvestigation(investigationUuid!)
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["investigation", investigationUuid]});
            toast({
                title: "Status updated",
                description: `Investigation has been marked as ${
                    investigation?.resolvedAt ? "active" : "resolved"
                }.`,
            });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: () =>
            withErrorHandling(() => deleteInvestigation(investigationUuid!)),
        onSuccess: () => {
            toast({
                title: "Investigation deleted",
                description: "The investigation has been deleted successfully.",
            });
            navigate("/investigations");
        },
    });

    const handleTitleEdit = () => {
        if (investigation) {
            setEditedTitle(investigation.title);
            setIsEditingTitle(true);
        }
    };

    const handleTitleSave = () => {
        if (editedTitle.trim() && editedTitle !== investigation?.title) {
            updateMutation.mutate({title: editedTitle.trim()});
        }
        setIsEditingTitle(false);
    };

    const handleDetailsEdit = () => {
        if (investigation) {
            setEditedDetails(investigation.markdown);
            setIsEditingDetails(true);
            setDetailsEditMode("write");
        }
    };

    const handleDetailsSave = () => {
        if (editedDetails.trim() && editedDetails !== investigation?.markdown) {
            updateMutation.mutate({markdown: editedDetails.trim()});
        }
        setIsEditingDetails(false);
        setDetailsEditMode("write");
    };

    const handleAddTag = () => {
        if (newTagKey.trim() && newTagValue.trim() && investigation) {
            const updatedTags = {
                ...investigation.tags,
                [newTagKey.trim()]: newTagValue.trim()
            };
            updateMutation.mutate({tags: updatedTags});
            setNewTagKey("");
            setNewTagValue("");
            setIsAddingTag(false);
        }
    };

    const handleRemoveTag = (key: string) => {
        if (investigation && investigation.tags) {
            const updatedTags = {...investigation.tags};
            delete updatedTags[key];
            updateMutation.mutate({tags: updatedTags});
        }
    };

    const handlePriorityChange = (newPriority: string) => {
        if (investigation) {
            const updatedTags = {
                ...investigation.tags,
                priority: newPriority
            };
            updateMutation.mutate({tags: updatedTags});
        }
    };

    const handleAssigneeChange = (assignee: DropDownItem) => {
        if (investigation) {
            const updatedTags = {
                ...investigation.tags,
                assignee: assignee.value
            };
            updateMutation.mutate({ tags: updatedTags });
        }
    };

    if (!investigationUuid) {
        return (
            <div className="flex justify-center items-center h-64">
                <p className="text-textdark">No investigation selected</p>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <LoadingSpinner/>
            </div>
        );
    }

    if (error || !investigation) {
        return (
            <div className="flex justify-center items-center h-64">
                <p className="text-destructive">Failed to load investigation</p>
            </div>
        );
    }

    const isResolved = !!investigation.resolvedAt;
    const status = isResolved ? "resolved" : "active";
    const priority = investigation.tags?.priority || "P3";
    const priorityConfig = PRIORITY_CONFIG[priority as keyof typeof PRIORITY_CONFIG] || PRIORITY_CONFIG.P3;
    const statusConfig = STATUS_CONFIG[status as keyof typeof STATUS_CONFIG];

    return (
        <div className="flex flex-col bg-backgrounddark">
            {/* GitHub-style Header */}
            <div className="">
                <div className="px-6 py-4">
                    {/* Title and status */}
                    <div className="flex items-start justify-between gap-4 mb-2">
                        <div className="flex-1">
                            {isEditingTitle ? (
                                <div className="flex items-center gap-2">
                                    <Input
                                        value={editedTitle}
                                        onChange={(e) => setEditedTitle(e.target.value)}
                                        className="text-3xl font-semibold bg-backgrounddark border-border text-textlight"
                                        autoFocus
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") handleTitleSave();
                                            if (e.key === "Escape") setIsEditingTitle(false);
                                        }}
                                    />
                                    <Button size="icon" variant="ghost" onClick={handleTitleSave}>
                                        <Check className="h-4 w-4 text-green-500"/>
                                    </Button>
                                    <Button size="icon" variant="ghost" onClick={() => setIsEditingTitle(false)}>
                                        <X className="h-4 w-4 text-red-500"/>
                                    </Button>
                                </div>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <h1
                                        className="text-3xl font-semibold text-textlight hover:text-primary cursor-pointer transition-colors"
                                        onClick={handleTitleEdit}
                                    >
                                        {investigation.title}
                                        <span className="text-textdark ml-2">#{investigation.uuid.slice(0, 8)}</span>
                                    </h1>
                                    {investigation.chatHistoryUuid && (
                                        <Badge
                                            variant="secondary"
                                            className="gap-1 bg-purple-600/20 text-purple-400 border-purple-600/40 border"
                                        >
                                            <Bot className="h-3 w-3" />
                                            AI Investigation
                                        </Badge>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Meta info */}
                    <div className="flex items-center gap-4 text-sm">
                        <Badge
                            className={cn(
                                "gap-1",
                                isResolved ? "bg-purple-600/20 text-purple-400 border-purple-600/40" : "bg-green-600/20 text-green-400 border-green-600/40",
                                "border"
                            )}
                        >
                            {isResolved ? (
                                <>
                                    <GitMerge className="h-3 w-3"/>
                                    Resolved
                                </>
                            ) : (
                                <>
                                    <GitPullRequest className="h-3 w-3"/>
                                    Open
                                </>
                            )}
                        </Badge>
                        <div className="flex items-center gap-1 text-textmedium">
                            <span className="font-medium">{investigation.creator}</span>
                            <span className="text-textdark">opened this</span>
                            <span
                                className="text-textdark">{format(new Date(investigation.createdAt), "MMM d, yyyy 'at' h:mm a")}</span>
                        </div>
                        {isResolved && investigation.resolvedAt && (
                            <div className="flex items-center gap-1 text-textdark">
                                <span>·</span>
                                <span>Resolved {format(new Date(investigation.resolvedAt), "MMM d, yyyy")}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Tabs */}
                <div className="px-6">
                    <Tabs defaultValue="conversation" className="w-full">
                        <TabsList
                            className="h-auto p-0 bg-transparent border-0 border-b border-border w-full justify-start">
                            <TabsTrigger
                                value="conversation"
                                className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:-mb-px rounded-none px-4 py-3 font-medium text-textmedium data-[state=active]:text-textlight"
                            >
                                <MessageSquare className="h-4 w-4 mr-2"/>
                                Conversation
                                <span
                                    className="ml-2 px-2 py-0.5 text-xs rounded-full bg-backgroundmedium text-textdark">{comments.length + 1}</span>
                            </TabsTrigger>
                            { investigation.chatHistoryUuid && (
                            <TabsTrigger
                                value="evidence"
                                className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:-mb-px rounded-none px-4 py-3 font-medium text-textmedium data-[state=active]:text-textlight"
                            >
                                <FileText className="h-4 w-4 mr-2"/>
                                Full AI Investigation
                                <span
                                    className="ml-2 px-2 py-0.5 text-xs rounded-full bg-backgroundmedium text-textdark">{aiInvestigationMessages.length}</span>
                            </TabsTrigger>)}
                        </TabsList>

                        {/* Tab Content */}
                        <div className="flex-1">
                            <TabsContent value="conversation" className="mt-0">
                                <div className="px-2 py-4">
                                    <div className="grid grid-cols-12 gap-6">
                                        {/* Main Content Area */}
                                        <div className="col-span-9">
                                            {/* Initial Description */}
                                            <div className="border border-border rounded-lg mb-4">
                                                <div
                                                    className="px-4 py-3 bg-backgroundmedium border-b border-border rounded-t-lg">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-3">
                                                            <div
                                                                className="h-8 w-8 rounded-full bg-backgroundlight flex items-center justify-center">
                                                                <User className="h-4 w-4 text-textdark"/>
                                                            </div>
                                                            <div>
                                                                <span
                                                                    className="font-medium text-textlight">{investigation.creator}</span>
                                                                <span
                                                                    className="text-textdark ml-2">commented on {format(new Date(investigation.createdAt), "MMM d, yyyy")}</span>
                                                            </div>
                                                        </div>
                                                        <DropdownMenu>
                                                            <DropdownMenuTrigger asChild>
                                                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                                                    <MoreHorizontal className="h-4 w-4"/>
                                                                </Button>
                                                            </DropdownMenuTrigger>
                                                            <DropdownMenuContent align="end"
                                                                                 className="bg-backgroundmedium border-border">
                                                                <DropdownMenuItem
                                                                    onClick={handleDetailsEdit}
                                                                    className="text-textmedium hover:text-textlight"
                                                                >
                                                                    <Edit2 className="h-4 w-4 mr-2"/>
                                                                    Edit
                                                                </DropdownMenuItem>
                                                            </DropdownMenuContent>
                                                        </DropdownMenu>
                                                    </div>
                                                </div>
                                                <div className="p-4">
                                                    {isEditingDetails ? (
                                                        <div className="space-y-2">
                                                            <div className="border border-border rounded-md">
                                                                <div className="flex border-b border-border">
                                                                    <button
                                                                        className={cn(
                                                                            "px-4 py-2 text-sm font-medium border-r border-border",
                                                                            detailsEditMode === "write"
                                                                                ? "text-textlight bg-backgrounddark"
                                                                                : "text-textdark hover:text-textlight"
                                                                        )}
                                                                        onClick={() => setDetailsEditMode("write")}
                                                                    >
                                                                        Write
                                                                    </button>
                                                                    <button
                                                                        className={cn(
                                                                            "px-4 py-2 text-sm font-medium",
                                                                            detailsEditMode === "preview"
                                                                                ? "text-textlight bg-backgrounddark"
                                                                                : "text-textdark hover:text-textlight"
                                                                        )}
                                                                        onClick={() => setDetailsEditMode("preview")}
                                                                    >
                                                                        Preview
                                                                    </button>
                                                                </div>
                                                                {detailsEditMode === "write" ? (
                                                                    <Textarea
                                                                        value={editedDetails}
                                                                        onChange={(e) => setEditedDetails(e.target.value)}
                                                                        className="min-h-[200px] bg-backgrounddark border-0 rounded-none text-textmedium focus:ring-0"
                                                                        placeholder="Leave a comment"
                                                                        autoFocus
                                                                    />
                                                                ) : (
                                                                    <div
                                                                        className="p-4 min-h-[200px] text-textmedium">
                                                                        <MarkdownRenderer content={editedDetails || "*No content*"} />
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="flex gap-2 justify-end">
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    className={"border border-border"}
                                                                    onClick={() => {
                                                                        setEditedDetails(investigation.markdown);
                                                                        setIsEditingDetails(false);
                                                                        setDetailsEditMode("write");
                                                                    }}
                                                                >
                                                                    Cancel
                                                                </Button>
                                                                <Button
                                                                    size="sm"
                                                                    onClick={handleDetailsSave}
                                                                    className="bg-green-600 hover:bg-green-700"
                                                                >
                                                                    Update comment
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="text-textmedium p-2">
                                                            <MarkdownRenderer content={investigation.markdown || "*No description provided.*"} />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Comments */}
                                            <InvestigationComments investigationUuid={investigationUuid}/>
                                        </div>

                                        {/* Sidebar */}
                                        <div className="col-span-3 space-y-4">
                                            {/* Actions */}
                                            <div className="border border-border rounded-lg p-4">
                                                <div className="space-y-3">
                                                    <Button
                                                        onClick={() => resolveMutation.mutate(!isResolved)}
                                                        disabled={resolveMutation.isPending}
                                                        variant={isResolved ? "outline" : "default"}
                                                        className={cn(
                                                            "w-full justify-center",
                                                            isResolved ? "" : "bg-green-600 hover:bg-green-700"
                                                        )}
                                                    >
                                                        {isResolved ? (
                                                            <>
                                                                <GitPullRequest className="h-4 w-4 mr-2"/>
                                                                Reopen investigation
                                                            </>
                                                        ) : (
                                                            <>
                                                                <CheckCircle className="h-4 w-4 mr-2"/>
                                                                Close investigation
                                                            </>
                                                        )}
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        onClick={() => setShowDeleteDialog(true)}
                                                        className="w-full text-red-400 hover:text-red-300 hover:bg-red-900/20 border-red-900/50 bg-redtransparenter"
                                                    >
                                                        <Trash2 className="h-4 w-4 mr-2"/>
                                                        Delete investigation
                                                    </Button>
                                                </div>
                                            </div>

                                            {/* Assignees */}
                                            <div className="border-b border-border pb-4">
                                                <div className="flex items-center justify-between mb-2">
                                                    <h3 className="text-sm font-medium text-textlight">Assignees</h3>
                                                </div>
                                                <div className="text-sm">
                                                    {loadingMembers ? (
                                                        <span className="text-textdark">Loading...</span>
                                                    ) : (
                                                        <div>
                                                            <SingleSelectorSearchableDropdown
                                                                possibleItems={organizationMembers}
                                                                selected={
                                                                    organizationMembers.find(
                                                                        member => member.value === investigation.tags?.assignee
                                                                    ) || { displayName: "Unassigned", value: "" }
                                                                }
                                                                setSelected={handleAssigneeChange}
                                                                searchPlaceholder="Search users..."
                                                                noMatchString="No users found"
                                                                disableWildcard={true}
                                                                displaySettings={{
                                                                    smallSize: true,
                                                                    borderClassName: "border-border",
                                                                    className: "bg-backgrounddark text-textmedium"
                                                                }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Labels */}
                                            <div className="border-b border-border pb-4">
                                                <div className="flex items-center justify-between mb-2">
                                                    <h3 className="text-sm font-medium text-textlight">Labels</h3>
                                                </div>
                                                <div className="flex flex-wrap gap-2">
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <button className="focus:outline-none">
                                                                <Badge
                                                                    variant="outline"
                                                                    className={cn("gap-1 border cursor-pointer", priorityConfig.textColor, `border-current`)}
                                                                >
                                                                    <priorityConfig.icon className="h-3 w-3"/>
                                                                    {priority}
                                                                </Badge>
                                                            </button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent
                                                            className="bg-backgroundmedium border-border">
                                                            {Object.entries(PRIORITY_CONFIG).map(([key, config]) => {
                                                                const Icon = config.icon;
                                                                return (
                                                                    <DropdownMenuItem
                                                                        key={key}
                                                                        onClick={() => handlePriorityChange(key)}
                                                                        className="text-textmedium hover:text-textlight"
                                                                    >
                                                                        <Icon
                                                                            className={cn("h-4 w-4 mr-2", config.textColor)}/>
                                                                        {key} - {config.label}
                                                                    </DropdownMenuItem>
                                                                );
                                                            })}
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                    {investigation.tags && Object.entries(investigation.tags)
                                                        .filter(([key]) => key !== "priority" && key !== "assignee")
                                                        .map(([key, value]) => (
                                                            <div key={key} className="group relative">
                                                                <TagComponent
                                                                    tagKey={key}
                                                                    tagValue={value}
                                                                />
                                                                <button
                                                                    onClick={() => handleRemoveTag(key)}
                                                                    className="absolute -top-1 -right-1 hidden group-hover:flex items-center justify-center w-4 h-4 rounded-full bg-red-600 hover:bg-red-700 text-white"
                                                                >
                                                                    <X className="h-2.5 w-2.5"/>
                                                                </button>
                                                            </div>
                                                        ))}
                                                    {isAddingTag ? (
                                                        <div className="flex items-center gap-1">
                                                            <Input
                                                                placeholder="key"
                                                                value={newTagKey}
                                                                onChange={(e) => setNewTagKey(e.target.value)}
                                                                className="h-6 w-20 text-xs bg-backgrounddark border-border"
                                                                autoFocus
                                                                onKeyDown={(e) => {
                                                                    if (e.key === "Enter" && newTagKey) {
                                                                        document.getElementById("tag-value-input")?.focus();
                                                                    }
                                                                    if (e.key === "Escape") {
                                                                        setIsAddingTag(false);
                                                                        setNewTagKey("");
                                                                        setNewTagValue("");
                                                                    }
                                                                }}
                                                            />
                                                            <span className="text-textdark">:</span>
                                                            <Input
                                                                id="tag-value-input"
                                                                placeholder="value"
                                                                value={newTagValue}
                                                                onChange={(e) => setNewTagValue(e.target.value)}
                                                                className="h-6 w-20 text-xs bg-backgrounddark border-border"
                                                                onKeyDown={(e) => {
                                                                    if (e.key === "Enter") handleAddTag();
                                                                    if (e.key === "Escape") {
                                                                        setIsAddingTag(false);
                                                                        setNewTagKey("");
                                                                        setNewTagValue("");
                                                                    }
                                                                }}
                                                            />
                                                            <Button size="icon" variant="ghost" className="h-6 w-6"
                                                                    onClick={handleAddTag}>
                                                                <Check className="h-3 w-3 text-green-500"/>
                                                            </Button>
                                                            <Button
                                                                size="icon"
                                                                variant="ghost"
                                                                className="h-6 w-6"
                                                                onClick={() => {
                                                                    setIsAddingTag(false);
                                                                    setNewTagKey("");
                                                                    setNewTagValue("");
                                                                }}
                                                            >
                                                                <X className="h-3 w-3 text-red-500"/>
                                                            </Button>
                                                        </div>
                                                    ) : (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-6 px-2"
                                                            onClick={() => setIsAddingTag(true)}
                                                        >
                                                            <Plus className="h-3 w-3"/>
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </TabsContent>
                            <TabsContent value="evidence" className="mt-0">
                                <AiInvestigation chatHistoryUuid={investigation.chatHistoryUuid}/>
                            </TabsContent>
                        </div>
                    </Tabs>
                </div>
            </div>

            {/* Delete confirmation dialog */}
            <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <AlertDialogContent className="bg-backgroundmedium border-border">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-textlight">Delete Investigation</AlertDialogTitle>
                        <AlertDialogDescription className="text-textdark">
                            Are you sure you want to delete this investigation? This action cannot be
                            undone and will also delete all associated comments.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="border-border text-textmedium">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => deleteMutation.mutate()}
                            className="bg-redtransparenter border-red-500 text-textmedium border"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default InvestigationDetails;